/*
  Kids Cafe offer widget (A/B test, Sep 2026)
  ===========================================
  Drives snippets/kc-offer-widget.liquid: plan + flavor selection, the per-pouch
  customiser, gift locks, the gallery bridge, and the cart.

  WHAT THE CART LOOKS LIKE (this is the part that decides what the customer pays)

    A / 1 Month      1 pouch $35                        + cape + mask + cup
    A / Buy 2 Get 1  3 pouches $105                     + cape + mask + cup
    B / 1 Month      1 pouch $35                        + cape + mask + cup
    B / Buy 2 Get 1  2 pouches $70 + Zenbean bag $41    + cape + mask + cup
                     ("Anti-Crash Focus Coffee", the $41 live Zenbean offer bag)

  Nothing is added at $0 -- the 9 Sep 2026 free-order incident came from $0 products
  being orderable by anyone through a cart permalink. Every gift keeps its real price
  and three automatic Buy X Get Y discounts zero them, which is also what turns $161
  into $70 on A and $167 into $70 on B:

    Kids Cafe offer: hero kit (1 Month)            buy 1 pouch  -> 3 free (kit)
    Kids Cafe offer A: free 3rd pouch + hero kit   buy 2 pouches-> 4 free (pouch + kit)
    Kids Cafe offer B: free Zenbean bag + hero kit buy 2 pouches-> 4 free (bag + kit)

  A BXGY pays nothing unless every one of its "gets" items is in the cart, and Shopify
  applies only the single best automatic discount per order. That is what keeps the two
  bundle discounts from stealing each other's carts: the A discount can only find 3 of
  its 4 items in a B cart and vice versa, so each cart resolves to its own discount.
  If the gift set here ever changes, the matching discount must change with it or the
  customer is charged for a "free" gift.

  ONE GIFT SET PER ORDER. The CTA clears any previous offer lines before adding, so
  clicking it twice cannot stack kits, and assets/zen-gift-guard.js reconciles the
  whole cart after every mutation as a second line of defence.
*/
(function () {
  "use strict";

  var MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  /* Keys that mark a line as belonging to this offer. Used to clear a previous
     selection before adding a new one -- never to recognise gifts for the guard,
     which owns that rule. */
  var OFFER_LINE_PROPS = [
    "_kc_offer",
    "_kids_cafe_plan",
    "_kids_cafe_pouch_index",
    "_kids_cafe_welcome_kit",
    "_kids_cafe_welcome_gift",
    "_kids_cafe_adult_coffee_gift"
  ];

  function money(n) {
    return "$" + (Math.round(Number(n) * 100) / 100).toFixed(2);
  }

  function whole(n) {
    return "$" + Math.round(Number(n));
  }

  function deliveryDate() {
    var d = new Date();
    d.setDate(d.getDate() + 5);
    return MONTHS[d.getMonth()] + " " + d.getDate();
  }

  function track(event, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, payload));
      document.dispatchEvent(new CustomEvent(event, { detail: payload }));
    } catch (e) {
      /* analytics must never block a sale */
    }
  }

  function init(widget) {
    if (widget.getAttribute("data-kc-offer-ready") === "true") return;

    var configTag = widget.querySelector("[data-kc-offer-config]");
    var config;
    try {
      config = JSON.parse(configTag.textContent);
    } catch (e) {
      console.error("Kids Cafe offer widget: bad config", e);
      return;
    }

    var arm = config.arm === "B" ? "B" : "A";
    var pouches = Number(config.pouches) || 3;
    var flavors = config.flavors || [];
    var bySlug = {};
    flavors.forEach(function (f) { bySlug[f.slug] = f; });

    var fallbackFlavor = (flavors[0] && flavors[0].slug) || "vanilla";
    var defaultFlavor = bySlug[config.defaultFlavor] ? config.defaultFlavor : fallbackFlavor;

    var state = {
      /* Buy 2 Get 1 Free is the preselected plan on both arms -- Naeem, via Jose,
         17 Sep: "for both let's make buy 2 get 1 free the auto toggled option". The
         markup ships in that state too, so there is no flicker from single to
         bundle on load. */
      plan: "bundle",
      mainFlavor: defaultFlavor,
      pouchFlavors: []
    };
    for (var i = 0; i < pouches; i++) state.pouchFlavors.push(defaultFlavor);

    var el = {
      pNow: widget.querySelector('[data-kcw="pNow"]'),
      pWas: widget.querySelector('[data-kcw="pWas"]'),
      pBadge: widget.querySelector('[data-kcw="pBadge"]'),
      savingLine: widget.querySelector('[data-kcw="savingLine"]'),
      savingText: widget.querySelector('[data-kcw="savingText"]'),
      delivery: widget.querySelector('[data-kcw="deliveryLine"]'),
      cta: widget.querySelector('[data-kcw="cta"]'),
      pick: widget.querySelector('[data-kcw="pick"]'),
      pickSum: widget.querySelector('[data-kcw="pickSum"]'),
      customizeBtn: widget.querySelector('[data-kcw="customizeBtn"]'),
      reset: widget.querySelector('[data-kcw="resetFlavors"]'),
      sumNames: widget.querySelector('[data-kcw="sumNames"]')
    };

    /* Both arms are in the DOM; only one is visible. Shared page furniture (the
       sticky bar, the gallery, analytics) is written by the visible arm only. */
    var shownArm = (document.documentElement.getAttribute("data-kc-offer") || "a").toUpperCase();
    var isVisibleArm = shownArm === arm;
    var lastGalleryFlavor = null;

    var planEls = widget.querySelectorAll("[data-kcw-plan]");
    var flavorEls = widget.querySelectorAll("[data-kcw-flavor]");
    var slotEls = widget.querySelectorAll("[data-kcw-slot]");
    var sumThumbs = widget.querySelectorAll('[data-kcw="sumThumb"]');

    function priceFor(plan) {
      return plan === "bundle" ? config.prices.bundle : config.prices.single;
    }

    function flavorSummary(list) {
      var counts = {};
      var order = [];
      list.forEach(function (slug) {
        if (!counts[slug]) { counts[slug] = 0; order.push(slug); }
        counts[slug] += 1;
      });
      return order.map(function (slug) {
        return counts[slug] + " " + (bySlug[slug] ? bySlug[slug].name : slug);
      }).join(" + ");
    }

    function selectedFlavors() {
      return state.plan === "bundle" ? state.pouchFlavors.slice() : [state.mainFlavor];
    }

    function render() {
      /* plans */
      planEls.forEach(function (node) {
        var on = node.getAttribute("data-kcw-plan") === state.plan;
        node.classList.toggle("sel", on);
        node.classList.toggle("active", on);
        if (node.hasAttribute("role") && node.getAttribute("role") === "radio") {
          node.setAttribute("aria-checked", on ? "true" : "false");
        } else {
          node.setAttribute("aria-pressed", on ? "true" : "false");
        }
      });

      /* flavor tiles */
      flavorEls.forEach(function (node) {
        var on = node.getAttribute("data-kcw-flavor") === state.mainFlavor;
        node.classList.toggle("active", on);
        node.setAttribute("aria-checked", on ? "true" : "false");
      });

      /* arm B top price row */
      var isBundle = state.plan === "bundle";
      if (el.pNow) el.pNow.textContent = money(priceFor(state.plan));
      if (el.pWas) {
        el.pWas.textContent = isBundle ? money(config.prices.bundleCompare) : "";
        el.pWas.style.display = isBundle ? "" : "none";
      }
      if (el.pBadge) {
        /* "Save 60%" on B, "Save 33%" on A -- the badge is a string in config so the
           page never recomputes a percentage the creative already fixed. */
        el.pBadge.textContent = config.prices.bundleSaveBadge || ("Save " + whole(config.prices.bundleSaving));
        el.pBadge.style.display = isBundle ? "" : "none";
      }

      /* arm B saving line */
      if (el.savingLine) {
        if (isBundle) {
          if (el.savingText) {
            el.savingText.textContent = "You're saving " + money(config.prices.bundleSaving) + " and shipping is FREE on this order";
          }
          el.savingLine.classList.add("show");
        } else {
          el.savingLine.classList.remove("show");
        }
      }

      /* gift locks: anything that needs the bundle greys out on 1 Month */
      widget.querySelectorAll("[data-kcw-need]").forEach(function (node) {
        node.classList.toggle("off", node.getAttribute("data-kcw-need") === "bundle" && !isBundle);
      });

      /* Per-pouch picker. On arm A it lives inside the bundle plan's body, which is
         already collapsed unless that plan is selected, so it stays shown. On arm B
         it sits on its own and only appears for the bundle. */
      if (el.pick && !el.pick.closest("[data-kcw-plan]")) {
        el.pick.classList.toggle("show", isBundle);
      }

      slotEls.forEach(function (slot, index) {
        var slug = state.pouchFlavors[index] || state.mainFlavor;
        var flavor = bySlug[slug];
        if (!flavor) return;
        var img = slot.querySelector(".slot-img");
        var name = slot.querySelector(".slot-info strong");
        if (img) img.src = flavor.img;
        if (name) name.textContent = flavor.name;
        slot.querySelectorAll(".opt").forEach(function (opt) {
          opt.classList.toggle("sel", opt.getAttribute("data-flavor") === slug);
        });
      });

      sumThumbs.forEach(function (img, index) {
        var flavor = bySlug[state.pouchFlavors[index] || state.mainFlavor];
        if (flavor) img.src = flavor.img;
      });
      if (el.sumNames) el.sumNames.textContent = flavorSummary(state.pouchFlavors);

      /* delivery + CTA */
      var date = deliveryDate();
      if (el.delivery) {
        el.delivery.innerHTML = isBundle
          ? "Delivered on <b>" + date + "</b> with Free Shipping"
          : "Delivered on <b>" + date + "</b>, shipping calculated at checkout";
      }
      if (el.cta) {
        el.cta.textContent = "Get Started - " + money(priceFor(state.plan));
        el.cta.setAttribute("data-plan", state.plan);
        el.cta.setAttribute("data-flavors", selectedFlavors().join(","));
      }

      /* Gallery: six listing images per flavor. Only on an actual flavor change --
         calling it on every render would snap the customer back to image 1 every
         time they switched plan. */
      if (isVisibleArm && lastGalleryFlavor !== state.mainFlavor && typeof widget.kcOfferGallery === "function") {
        lastGalleryFlavor = state.mainFlavor;
        widget.kcOfferGallery(state.mainFlavor);
      }

      /* Sticky mobile bar (kcpc-header). It is shared page furniture, so only the
         arm the visitor can see writes to it. */
      if (isVisibleArm) {
        var stickyPrice = document.getElementById("zbpc-sticky-price");
        var stickyOrig = document.getElementById("zbpc-sticky-orig");
        var stickySave = document.getElementById("zbpc-sticky-save");
        var stickyLabel = document.getElementById("zbpc-sticky-label");
        if (stickyPrice) stickyPrice.textContent = money(priceFor(state.plan));
        if (stickyOrig) {
          stickyOrig.textContent = isBundle ? money(config.prices.bundleCompare) : "";
          stickyOrig.style.display = isBundle ? "" : "none";
        }
        if (stickySave) stickySave.textContent = isBundle ? (config.prices.bundleSaveBadge || "") : "";
        if (stickyLabel) stickyLabel.textContent = isBundle ? "Buy 2, Get 1 FREE" : "1 Month Supply";
      }
    }

    /* ---- option trays, built once ---- */
    slotEls.forEach(function (slot, index) {
      var tray = slot.querySelector(".slot-opts");
      var change = slot.querySelector('[data-kcw="slotChange"]');
      if (!tray || !change) return;

      flavors.forEach(function (flavor) {
        var opt = document.createElement("button");
        opt.type = "button";
        opt.className = "opt";
        opt.setAttribute("data-flavor", flavor.slug);
        opt.innerHTML = '<img src="' + flavor.img + '" alt="" width="72" height="72" loading="lazy" decoding="async"><span>' + flavor.name + "</span>";
        opt.addEventListener("click", function (event) {
          event.stopPropagation();
          state.pouchFlavors[index] = flavor.slug;
          tray.classList.remove("open");
          render();
        });
        tray.appendChild(opt);
      });

      change.addEventListener("click", function (event) {
        event.stopPropagation();
        widget.querySelectorAll(".slot-opts.open").forEach(function (other) {
          if (other !== tray) other.classList.remove("open");
        });
        tray.classList.toggle("open");
      });
    });

    /* ---- selection ---- */
    flavorEls.forEach(function (node) {
      node.addEventListener("click", function () {
        state.mainFlavor = node.getAttribute("data-kcw-flavor");
        state.pouchFlavors = state.pouchFlavors.map(function () { return state.mainFlavor; });
        render();
      });
    });

    planEls.forEach(function (node) {
      function choose(event) {
        /* clicks inside the picker belong to the picker, not the plan card */
        if (event.target.closest(".change") || event.target.closest(".opt") || event.target.closest(".reset")) return;
        state.plan = node.getAttribute("data-kcw-plan");
        render();
      }
      node.addEventListener("click", choose);
      node.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " ") { event.preventDefault(); choose(event); }
      });
    });

    if (el.pickSum) {
      el.pickSum.addEventListener("click", function (event) {
        if (event.target.closest(".reset")) return;
        event.stopPropagation();
        el.pick.classList.toggle("open");
        if (el.customizeBtn) el.customizeBtn.textContent = el.pick.classList.contains("open") ? "Done" : "Customize";
      });
    }

    if (el.reset) {
      el.reset.addEventListener("click", function (event) {
        event.stopPropagation();
        state.pouchFlavors = state.pouchFlavors.map(function () { return state.mainFlavor; });
        render();
      });
    }

    /* ---- cart ---- */
    function cartJson(url, body) {
      return fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body)
      }).then(function (response) {
        if (!response.ok) {
          return response.json().catch(function () { return {}; }).then(function (error) {
            throw new Error((error && (error.description || error.message)) || "Cart request failed");
          });
        }
        return response.json();
      });
    }

    /* Zero every line from a previous click so a second CTA press replaces the
       selection instead of stacking a second bundle and a second gift set. */
    function clearPreviousOffer() {
      return fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          var updates = {};
          (cart.items || []).forEach(function (item) {
            var props = item.properties || {};
            var mine = OFFER_LINE_PROPS.some(function (key) {
              return props[key] !== undefined && props[key] !== null && props[key] !== "";
            });
            if (mine) updates[item.key] = 0;
          });
          if (!Object.keys(updates).length) return null;
          return cartJson("/cart/update.js", { updates: updates });
        })
        .catch(function () { return null; });
    }

    function buildItems() {
      var list = selectedFlavors();
      var planLabel = state.plan === "bundle" ? "Buy 2 Get 1 Free" : "1 Month Supply";
      var items = [];

      list.forEach(function (slug, index) {
        var flavor = bySlug[slug] || bySlug[state.mainFlavor];
        if (!flavor || !flavor.variantId) return;
        var props = {
          _kids_cafe_welcome_kit: "true",
          _kids_cafe_plan: planLabel,
          _kids_cafe_pouch_index: String(index + 1),
          _kc_offer: arm
        };
        /* Marks the purchase that earns the free Zenbean bag on arm B. */
        if (arm === "B" && state.plan === "bundle") props._kc_adult_coffee = "true";
        items.push({ id: flavor.variantId, quantity: 1, properties: props });
      });

      (config.gifts.kit || []).forEach(function (gift) {
        if (!gift.variantId) return;
        items.push({
          id: gift.variantId,
          quantity: 1,
          properties: {
            Type: "Free gift with your order",
            _kids_cafe_welcome_gift: "true",
            _kc_offer: arm
          }
        });
      });

      if (arm === "B" && state.plan === "bundle" && config.gifts.zenbean && config.gifts.zenbean.variantId) {
        items.push({
          id: config.gifts.zenbean.variantId,
          quantity: 1,
          properties: {
            Type: "Free gift with your order",
            _kids_cafe_adult_coffee_gift: "true",
            _kc_offer: arm
          }
        });
      }

      return items;
    }

    if (el.cta) {
      el.cta.addEventListener("click", function (event) {
        event.preventDefault();
        if (el.cta.disabled) return;

        var payload = {
          plan: el.cta.getAttribute("data-plan"),
          flavors: el.cta.getAttribute("data-flavors").split(","),
          arm: arm,
          price: priceFor(state.plan)
        };
        console.log("checkout payload", payload);

        var items = buildItems();
        if (!items.length) {
          alert("Sorry, this offer is not available right now. Please refresh and try again.");
          return;
        }

        el.cta.disabled = true;
        track("kc_offer_add_to_cart", {
          kc_offer_arm: arm,
          kc_offer_plan: payload.plan,
          kc_offer_flavors: payload.flavors.join(","),
          value: payload.price
        });

        clearPreviousOffer()
          // First pouch = the one Add to Cart; the other pouches and gifts go in without one (zen-cart-add.js).
          .then(function () { return window.ZenCartAdd ? window.ZenCartAdd.add(items) : cartJson("/cart/add.js", { items: items }); })
          .then(function () {
            var attributes = {};
            attributes[config.cartAttribute] = config.offerLabel;
            attributes["Kids Cafe plan"] = payload.plan === "bundle" ? "Buy 2 Get 1 Free" : "1 Month Supply";
            return cartJson("/cart/update.js", { attributes: attributes });
          })
          .then(function () { window.location.assign("/checkout"); })
          .catch(function (error) {
            console.error("Kids Cafe offer checkout error:", error);
            el.cta.disabled = false;
            alert("Sorry, something went wrong adding this to your cart:\n\n" + error.message + "\n\nPlease try again or contact support.");
          });
      });
    }

    /* The CTA disables itself while the add-to-cart is in flight. Browsers restore
       a page from the back/forward cache exactly as it was, so a customer who went
       to checkout and pressed Back came back to a dead button and had to reload --
       Jose's video, 17 Sep. Re-enable it whenever the page is shown or is about to
       be left. */
    function releaseCta() {
      if (!el.cta) return;
      el.cta.disabled = false;
      el.cta.style.opacity = "";
    }
    window.addEventListener("pageshow", releaseCta);
    window.addEventListener("pagehide", releaseCta);

    render();
    widget.setAttribute("data-kc-offer-ready", "true");

    /* Only the arm the visitor actually sees reports a view, or the test would read
       one view per arm on every page load. */
    if (isVisibleArm) track("kc_offer_view", { kc_offer_arm: arm });
  }

  function boot() {
    document.querySelectorAll("[data-kc-offer-widget]").forEach(init);
  }

  window.KcOfferWidget = { boot: boot };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
